import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { StatusPill } from "@/components/StatusPill";
import { ProgressRing } from "@/components/ProgressRing";
import {
  Check, Camera, Image as ImageIcon, ChevronRight, Plus, Send, AlertTriangle, MessageSquare, ArrowLeft, Globe, CheckCircle2,
} from "lucide-react";

const SEND_LANGS = [
  { code: "", label: "English (default)" },
  { code: "es", label: "Spanish" },
  { code: "pt", label: "Portuguese" },
  { code: "vi", label: "Vietnamese" },
  { code: "zh", label: "Chinese" },
  { code: "ht", label: "Haitian Creole" },
];

function OwnerItem({ item, index, onVerify, onFlag, onAddProof, onOpenPhoto }) {
  const verified = item.status === "done";
  const awaiting = !verified && !!item.proof;
  const isIssue = item.status === "issue";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: Math.min(index * 0.06, 0.3), ease: [0.22, 0.61, 0.36, 1] }}
      className={cn(
        "card-elevated relative overflow-hidden rounded-2xl border bg-card p-4 transition-colors",
        verified ? "border-emerald-500/40" : awaiting ? "border-amber-500/50" : isIssue ? "border-rose-500/40" : "border-border"
      )}
      data-testid={`demo-owner-item-${item.id}`}
    >
      <span className={cn("absolute inset-y-0 left-0 w-1", verified ? "bg-emerald-500" : awaiting ? "bg-amber-500" : "bg-transparent")} />

      <div className="flex items-start gap-3">
        <button
          type="button"
          data-testid={`demo-owner-verify-${item.id}`}
          onClick={() => onVerify(item)}
          aria-label={verified ? "Verified — tap to undo" : "Verify complete"}
          className={cn(
            "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-all active:scale-90",
            verified
              ? "border-emerald-500 bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
              : "border-muted-foreground/30 text-transparent hover:border-emerald-500 hover:text-emerald-500"
          )}
        >
          <Check className="h-4 w-4" strokeWidth={3} />
        </button>

        <div className="min-w-0 flex-1">
          <p className={cn("text-[15px] font-semibold leading-snug", verified && "text-muted-foreground line-through decoration-emerald-500/40")}>
            {item.title}
          </p>
          {verified ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-500">
              <Check className="h-3 w-3" /> Verified
            </span>
          ) : awaiting ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-500">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Proof in — tap ✓ to verify
            </span>
          ) : isIssue ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-500">Issue flagged</span>
          ) : (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Pending proof</span>
          )}
        </div>

        <button
          data-testid={`demo-owner-flag-${item.id}`}
          onClick={() => onFlag(item)}
          className={cn(
            "-mr-1 -mt-1 flex h-8 items-center gap-1 rounded-full px-2 text-[11px] font-semibold transition-colors",
            isIssue ? "text-rose-500" : "text-muted-foreground/60 hover:text-rose-500"
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" /> {isIssue ? "Flagged" : "Flag"}
        </button>
      </div>

      {/* Before → After split */}
      <div className="mt-4 flex items-stretch pl-10">
        <figure className="min-w-0 flex-1">
          <figcaption className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" /> Reference
          </figcaption>
          <button type="button" onClick={() => onOpenPhoto(item.ref)} className="group block aspect-square w-full overflow-hidden rounded-xl border border-border ring-1 ring-black/5" data-testid={`demo-owner-ref-${item.id}`}>
            <img src={item.ref} alt="" className="h-full w-full object-cover transition-transform duration-300 group-active:scale-[1.04]" />
          </button>
        </figure>

        <div className="relative mx-3 flex w-px shrink-0 items-center justify-center self-stretch bg-border">
          <span className={cn("flex h-6 w-6 items-center justify-center rounded-full border bg-card shadow-sm", item.proof ? "border-emerald-500/40 text-emerald-500" : "border-border text-muted-foreground")}>
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>

        <figure className="min-w-0 flex-1">
          <figcaption className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Proof
          </figcaption>
          {item.proof ? (
            <>
              <button type="button" onClick={() => onOpenPhoto(item.proof.url)} className="group block aspect-square w-full overflow-hidden rounded-xl border-2 border-emerald-500/60 ring-1 ring-emerald-500/10" data-testid={`demo-owner-proof-${item.id}`}>
                <img src={item.proof.url} alt="" className="h-full w-full object-cover transition-transform duration-300 group-active:scale-[1.04]" />
              </button>
              {item.proof.caption && (
                <p className="mt-1.5 flex items-start gap-1 rounded-lg bg-emerald-500/10 px-2 py-1.5 text-[12px] leading-snug text-emerald-100">
                  <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                  <span className="min-w-0">{item.proof.caption}</span>
                </p>
              )}
            </>
          ) : (
            <button
              type="button"
              data-testid={`demo-owner-add-proof-${item.id}`}
              onClick={() => onAddProof(item)}
              className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-emerald-500/50 bg-emerald-500/5 text-center text-[11px] font-semibold text-emerald-500 transition-all active:scale-[0.98] hover:bg-emerald-500/10"
            >
              <Camera className="h-5 w-5" />
              Add proof
              <span className="text-[10px] font-normal text-emerald-500/70">Tap to add</span>
            </button>
          )}
        </figure>
      </div>
    </motion.div>
  );
}

export function DemoOwner({ job, items, onVerify, onFlag, onAddItem, onAddProof, onOpenPhoto, onShare, onBack, mode = "owner", onFinish, sendLang = "", onSendLang }) {
  const isVerify = mode === "verify";
  const doneCount = items.filter((i) => i.status === "done").length;
  const orangeCount = items.filter((i) => i.status !== "done" && !!i.proof).length;
  const issueCount = items.filter((i) => i.status === "issue").length;
  const donePct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  return (
    <div className="mx-auto w-full max-w-md md:max-w-2xl px-4 pb-40 pt-4 sm:px-8" data-testid="demo-owner">
      <button onClick={onBack} data-testid="demo-owner-back" className="mb-1 -ml-1 flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Jobs
      </button>
      <h1 className="font-heading text-2xl font-extrabold leading-tight">{job.name}</h1>
      <div className="mt-1 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <StatusPill status="sent" />
          <span>· {items.length} items · {donePct}%</span>
          {issueCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-bold text-rose-500">
              <AlertTriangle className="h-3 w-3" /> {issueCount} flagged
            </span>
          )}
        </div>
        <ProgressRing total={items.length} green={doneCount} orange={orangeCount} red={issueCount} size={52} stroke={5} />
      </div>

      {!isVerify && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="demo-send-lang-row">
          <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
          <select
            data-testid="demo-send-lang-select"
            value={sendLang}
            onChange={(e) => onSendLang?.(e.target.value)}
            className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm font-medium focus:border-emerald-500 focus:outline-none"
          >
            {SEND_LANGS.map((x) => <option key={x.code || "en"} value={x.code}>{x.code ? `Send in ${x.label}` : x.label}</option>)}
          </select>
          {sendLang && <span className="text-xs text-muted-foreground">Worker opens this in <span className="font-semibold text-emerald-500">{SEND_LANGS.find((x) => x.code === sendLang)?.label}</span></span>}
        </div>
      )}

      {isVerify && (
        <div className="mt-3 flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3.5" data-testid="demo-verify-callout">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white"><CheckCircle2 className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-bold text-amber-500">Proof's back — this is where you verify</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Orange means the crew sent proof and it's waiting on you. Tap the ✓ on each item to approve.</p>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card px-6 py-14 text-center" data-testid="demo-owner-empty">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10"><Camera className="h-8 w-8 text-emerald-500" /></div>
          <h3 className="mt-4 font-heading text-lg font-bold">No items yet</h3>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">Tap Add Item and pick (or snap) a photo of each thing that needs proof.</p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((it, i) => (
            <OwnerItem key={it.id} index={i} item={it} onVerify={onVerify} onFlag={onFlag} onAddProof={onAddProof} onOpenPhoto={onOpenPhoto} />
          ))}
        </div>
      )}

      {!isVerify && (
        <button
          data-testid="demo-owner-add-item"
          onClick={onAddItem}
          className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-500/60 text-base font-semibold text-emerald-500 transition-transform active:scale-[0.99] hover:bg-emerald-500/10"
        >
          <Plus className="h-5 w-5" /> Add Item
        </button>
      )}

      {/* Fixed action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.7rem)] pt-2.5 backdrop-blur-xl sm:px-8">
        <div className="mx-auto max-w-2xl">
          {isVerify ? (
            <>
              <button
                data-testid="demo-owner-finish"
                onClick={onFinish}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 text-base font-bold text-white shadow-lg shadow-emerald-500/25 transition-colors hover:bg-emerald-600"
              >
                <Check className="h-5 w-5" /> Finish
              </button>
              <p className="mt-1.5 text-center text-[11px] text-muted-foreground">{doneCount === items.length ? "All verified — that's the whole loop" : "Verify each item with the ✓, then finish"}</p>
            </>
          ) : (
            <>
              <button
                data-testid="demo-owner-share"
                onClick={onShare}
                disabled={items.length === 0}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 text-base font-bold text-white shadow-lg shadow-emerald-500/25 transition-colors hover:bg-emerald-600 disabled:opacity-50 disabled:shadow-none"
              >
                <Send className="h-5 w-5" /> Send
              </button>
              <p className="mt-1.5 text-center text-[11px] text-muted-foreground">{items.length === 0 ? "Add at least one item first" : "Choose how the link goes out — one link, by trade, or PDF"}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
